import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
	Award, Building2, GraduationCap, MapPin, Package, Shield,
	ShoppingBag, Star, Truck, Users, CalendarDays, Trophy,
	Stethoscope, AlertCircle
} from "lucide-react";
import { formatEventDate } from "@/utils/dateUtil";

const OTHER = '__other__';

export default function SearchResultCard ({ ad, adType, uri, catMap, breedMap }) {
	const { t } = useTranslation();

	const calculateAge = (dob) => {
		if (!dob) return null;
		return Math.floor((new Date() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000));
	};

	const getGender = gender => {
		if (gender === 'mare') return t('horse.mare');
		if (gender === 'gelding') return t('horse.gelding');

		return t('horse.stallion');
	}

	return (
		<Link
			key={ ad.id }
			to={ `/${ uri }/${ ad.slug || ad.id }` }
			state={ { from: { path: location.pathname + location.search, label: t(`nav.${ uri }`) } } }
			className="horse-card group bg-white border border-[#E6E2D6] rounded-sm overflow-hidden"
			data-testid={ `horse-item-${ ad.id }` }
		>
			<div className="aspect-[4/3] bg-gradient-to-br from-[#1A3C34] to-[#8B4513] flex items-center justify-center relative">
				{ ad.images?.[0] ? (
					<img
						src={ ad.images[0].startsWith('http') ? ad.images[0] : `${ process.env.REACT_APP_BACKEND_URL }${ ad.images[0] }` }
						alt={ ad.title }
						className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
						loading="lazy"/>
				) : (
					<div className="w-full h-full flex items-center justify-center text-[#D4C5A9]">
						{ adType === 'horse' && <Star className="w-12 h-12"/> }
						{ adType === 'product' && <ShoppingBag className="w-12 h-12"/> }
						{ adType === 'transport' && <Truck className="w-16 h-16 text-white/80"/> }
						{ adType === 'school' && <GraduationCap className="w-16 h-16 text-white/80" /> }
						{ adType === 'stable' && <Building2 className="w-16 h-16 text-white/80" /> }
						{ adType === 'trainer' && <GraduationCap className="w-16 h-16 text-white/80" /> }
						{ adType === 'event' && <CalendarDays className="w-16 h-16 text-white/80" /> }
						{ adType === 'vet' && <Stethoscope className="w-16 h-16 text-white/80" /> }
					</div>
				) }
				{ adType === 'event' && ad.event_type !== null &&
					<span className="absolute bottom-3 left-3 text-[10px] bg-white/95 text-[#1A3C34] px-2 py-1 rounded-sm
							font-semibold uppercase tracking-wider">
						{t(`event.type.${ad.event_type || 'other'}`)}
					</span>
				}
				{ adType === 'vet' && ad.emergency_24_7 &&
					<span className="absolute bottom-3 left-3 text-[10px] bg-[#9B2C2C] text-white px-2 py-1 rounded-sm
							font-bold uppercase tracking-wider inline-flex items-center gap-1">
						<AlertCircle className="w-3 h-3" /> {t('vet.emergency_24_7_badge')}
					</span>
				}
				{ ad.is_featured && (
					<span	className="absolute top-3 left-3 featured-badge px-3 py-1 text-[10px] font-bold tracking-wider uppercase
					 		text-[#1A3C34] rounded-sm">
						{ t('horse.featured') }
					</span>
				) }
				{ ad.user_verified && (
					<span	className="absolute top-3 right-3 flex verified-badge gap-1 px-3 py-1 text-[10px] font-bold uppercase
							tracking-wider items-center rounded-sm">
						<Shield className="w-4 h-4"/>
						{ t('horse.verified') }
					</span>
				) }
			</div>
			<div className="p-5">
				{ adType === 'transport' &&
					<div className="flex items-center gap-2 mb-1 flex-wrap">
			 			<span className="text-[10px] font-bold text-[#8B4513] uppercase tracking-wider">
			 				{ t(`transport.vehicle.${ ad.vehicle_type }`, ad.vehicle_type) }
			 			</span>
					</div>
				}
				{ adType === 'product' &&
					<span className="text-[10px] font-bold text-[#8B4513] uppercase tracking-wider">
			 			{ ad.category === OTHER ? ad.customCategory : catMap[ad.category] || ad.category }
			 		</span>
				}
				{ adType === 'school' &&
					(ad.disciplines || []).slice(0, 2).map(d => (
						<span key={d} className="text-[10px] font-bold text-[#8B4513] uppercase tracking-wider ml-1">
							{t(`school.discipline_opt.${d}`, d)}
						</span>
					))
				}
				{ adType === 'stable' &&
					(ad.boarding_types || []).slice(0, 2).map(b => (
						<span key={b} className="text-[10px] font-bold text-[#8B4513] uppercase tracking-wider">
							{t(`stable.boarding_opt.${b}`, b)}
						</span>
					))
				}
				{ adType === 'trainer' &&
					(ad.disciplines || []).slice(0, 2).map(d => (
						<span key={d} className="text-[10px] font-bold text-[#8B4513] uppercase tracking-wider">
							{t(`trainer.discipline_opt.${d}`, d)}
						</span>
					))
				}
				{ adType === 'event' &&
					<div className="flex items-center gap-2 mb-1 flex-wrap text-[10px] text-[#8B4513] uppercase tracking-wider font-bold">
						<CalendarDays className="w-3 h-3" />
						{formatEventDate(ad.start_date)}
						{ad.end_date && ad.end_date !== ad.start_date && <> – {formatEventDate(ad.end_date)}</>}
					</div>
				}
				<h3 className="text-lg font-semibold text-[#1A3C34] group-hover:text-[#8B4513] transition-colors
						line-clamp-2 font-['Playfair_Display']">
					{ ad.title }
				</h3>
				<div className="flex items-baseline gap-1 mt-2">
					{ (adType === 'horse' || adType === 'product' || adType === 'transport' || adType === 'school' ||
							adType === 'stable' || adType === 'trainer') &&
						<p className="text-2xl font-bold text-[#8B4513] mt-1 mb-3">&euro;{ ad.price?.toLocaleString() }</p>
					}
					{ adType === 'transport' &&
						<span className="text-xs text-[#666055]">
						/ { t(`transport.unit.${ ad.pricing_unit || 'trip' }`) }
					</span>
					}
					{ adType === 'school' &&
						<span className="text-xs text-[#666055]">
							/ {t(`school.unit.${ad.pricing_unit || 'lesson'}`)}
						</span>
					}
					{ adType === 'stable' &&
						<span className="text-xs text-[#666055]">
							/ {t(`stable.unit.${ad.pricing_unit || 'month'}`)}
						</span>
					}
					{ adType === 'trainer' &&
						<span className="text-xs text-[#666055]">
							/ {t(`school.unit.${ad.pricing_unit || 'lesson'}`)}
						</span>
					}
					{ adType === 'event' &&
						<div className="flex items-baseline gap-1 mt-2">
							{ad.is_free ? (
								<p className="text-2xl font-bold text-[#1A3C34]">{t('event.free')}</p>
							) : (
								<>
									<p className="text-2xl font-bold text-[#8B4513]">&euro;{ad.price?.toLocaleString()}</p>
									<span className="text-xs text-[#666055]">{t('event.entry')}</span>
								</>
							)}
						</div>
					}
					{ adType === 'vet' &&
						<div className="flex-1 flex flex-col">
							{ad.clinic_name && <p className="text-xs text-[#666055] mt-0.5">{ad.clinic_name}</p>}
							<div className="flex items-baseline gap-1 mt-2">
								<p className="text-2xl font-bold text-[#8B4513]">&euro;{(ad.price || 0).toLocaleString()}</p>
								<span className="text-xs text-[#666055]">{t('vet.consultation')}</span>
							</div>
							<div className="flex items-center gap-3 mt-2 text-xs text-[#666055]/80 flex-wrap">
								{typeof ad.experience_years === 'number' && ad.experience_years > 0 && (
									<span>{ad.experience_years} {t('vet.years_experience')}</span>
								)}
								{ ad.mobile_only &&
									<span className="inline-flex items-center gap-1">
										<Truck className="w-3 h-3" /> {t('vet.mobile_only_short')}
									</span>
								}
							</div>
							{(ad.specialties?.length || 0) > 0 && (
								<div className="flex flex-wrap gap-1 mt-3">
									{ad.specialties.slice(0, 3).map(s => (
										<span key={s} className="text-[10px] bg-[#F2F0E9] text-[#1A3C34] px-2 py-0.5 rounded-sm">
											{t(`vet.specialty.${s}`)}
										</span>
									))}
								</div>
							)}
						</div>
					}
				</div>
				{ adType === 'transport' &&
					<div className="flex items-center gap-3 mt-3 text-xs text-[#666055]/80 flex-wrap">
						{ ad.capacity > 0 && (
							<span className="inline-flex items-center gap-1">
			 					<Users className="w-3 h-3"/>{ ad.capacity } { t('transport.horses') }
			 				</span>
						) }
						{ ad.coverage_area && (
							<span className="truncate max-w-[160px]">{ ad.coverage_area }</span>
						) }
					</div>
				}
				{ adType === 'horse' &&
					<div className="flex flex-wrap items-center gap-3 text-xs text-[#666055]">
						{ ad.breed &&
							<span className="px-2 py-0.5 bg-[#F2F0E9] rounded-sm">
							{ ad.breed === OTHER ? ad.customBreed : breedMap[ad.breed] || ad.breed }
						</span>
						}
						{ ad.date_of_birth &&
							<span>{ calculateAge(ad.date_of_birth) } { t('horse.years') }</span>
						}
						{ ad.height &&
							<span>{ ad.height } { t('horse.cm') }</span>
						}
						{ ad.gender &&
							<span className="capitalize">{ getGender(ad.gender) }</span>
						}
					</div>
				}
				{ adType === 'school' &&
					<div className="flex items-center gap-3 mt-3 text-xs text-[#666055]/80 flex-wrap">
						{ad.has_school_horses && ad.school_horses_count > 0 && (
							<span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />
								{ad.school_horses_count} {t('school.school_horses_short')}
							</span>
						)}
					</div>
				}
				{ adType === 'stable' &&
					<div className="flex items-center gap-3 mt-3 text-xs text-[#666055]/80 flex-wrap">
						{typeof ad.available_boxes === 'number' && (
							<span className="inline-flex items-center gap-1"><Package className="w-3 h-3" />
								{ad.available_boxes}/{ad.total_boxes || '—'} {t('stable.boxes_short')}
							</span>
						)}
					</div>
				}
				{ adType === 'trainer' &&
					<div className="flex items-center gap-3 mt-3 text-xs text-[#666055]/80 flex-wrap">
						{ typeof ad.experience_years === 'number' && ad.experience_years > 0 && (
							<span className="inline-flex items-center gap-1">
								<Award className="w-3 h-3"/>
								{ ad.experience_years } { t('trainer.years') }
							</span>
						)}
						{ ad.travels_to_client &&
							<span className="text-[#8B4513]">· {t('trainer.travels_short')}</span>
						}
					</div>
				}
				{ adType === 'event' &&
					<div className="flex items-center gap-3 mt-3 text-xs text-[#666055]/80 flex-wrap">
						{ typeof ad.prize_amount === 'number' && ad.prize_amount > 0 && (
							<span className="inline-flex items-center gap-1">
								<Trophy className="w-3 h-3" />&euro;{ad.prize_amount?.toLocaleString()}
							</span>
						)}
						{ typeof ad.max_participants === 'number' && ad.max_participants > 0 && (
							<span className="inline-flex items-center gap-1">
								<Users className="w-3 h-3" />{ad.max_participants}
							</span>
						)}
					</div>
				}
				{ ad.location_name && (
					<div className="flex items-center gap-1 mt-3 text-xs text-[#666055]/70">
						<MapPin className="w-3 h-3"/> { ad.location_name }
						{ ad.distance_km != null &&
							<span className="ml-1 font-semibold text-[#8B4513]">({ ad.distance_km } km)</span>
						}
					</div>
				) }
			</div>
		</Link>
	);
};